import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer'
import { useDispatch, useSelector } from 'react-redux'
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer'
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile'
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer'
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer'
import updateProperties from '../../../utils/DataModifier/DataModifier'
import DataTable from '../../../components/common/DataTable/DataTable'
import TableRow, { TableCellData } from '../../../components/common/TableRow/TableRow'
import "./Utilities.css"
import { useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useNavigation } from '../../../utils/context/NavigationContext'
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages'
import { selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths'

interface AttributeDataType {
  "residentialHeatFlag": boolean | null,
  "residentialHydroFlag": boolean | null,
  "residentialWaterFlag": boolean | null,
  "residentialCableFlag": boolean | null,
  "residentialInternetFlag": boolean | null,
  "residentialPoolSaunaFitnessFlag": boolean | null,
  "residentialAirConditioningFlag": boolean | null,
  "residentialElevatorFlag": boolean | null,

  "residentialLaundryEnsuiteFlag": boolean | null,
  "residentialLaundryCommonFlag": boolean | null,

  "residentialStorageInternalFlag": boolean | null,
  "residentialStorageExternalFlag": boolean | null,

  "residentialAccessDoormanConciergeFlag": boolean | null,
  "residentialAccessControlledFlag": boolean | null,

  "residentialOnsiteSecurityFlag": boolean | null,
  "residentialOnsiteStaffFlag": boolean | null,
}
const initialAttributeData: AttributeDataType = {
  "residentialHeatFlag": null,
  "residentialHydroFlag": null,
  "residentialWaterFlag": null,
  "residentialCableFlag": null,
  "residentialInternetFlag": null,
  "residentialPoolSaunaFitnessFlag": null,
  "residentialAirConditioningFlag": null,
  "residentialElevatorFlag": null,

  "residentialLaundryEnsuiteFlag": null,
  "residentialLaundryCommonFlag": null,

  "residentialStorageInternalFlag": null,
  "residentialStorageExternalFlag": null,

  "residentialAccessDoormanConciergeFlag": null,
  "residentialAccessControlledFlag": null,

  "residentialOnsiteSecurityFlag": null,
  "residentialOnsiteStaffFlag": null,
}

function Utilities() {
  const dispatch = useDispatch()
  const { t } = useTranslation(['utilities', 'navigation'])
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
  const saveData: SaveDataState = useSelector(selectSaveData)
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const [attributeData, setAttributeData] = useState<AttributeDataType>(initialAttributeData)
  const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
  const isInternalUser = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const { errors, clearErrors } = useFormErrorContext();
  const { direction } = useNavigation();


  useEffect(() => {
    dispatch(resetGetProfileAttributes())
    dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!profileAttributes.loading && profileAttributes.data) {
      const initData = { ...initialAttributeData }
      updateProperties(initData, profileAttributes.data)
      setAttributeData(initData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileAttributes.data, profileAttributes.loading])

  const handleAttributeChange = (name: string, value: string | number | boolean | null) => {
    setAttributeData({
      ...attributeData,
      [name]: value
    })
  }

  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const data = { ...profileAttributes.data, ...attributeData } as Record<string, unknown>
      const triggerSpectral = profileOverview.profile.profile_type_code === "BIP" && (isInternalUser || direction !== "backward")

      dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId, {
        "profile_attributes": data,
        "spectral_section": "utilities",
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternalUser
      }))
    }
  }, [attributeData, currentSnapshotId, dispatch, profileAttributes.data, profileOverview.profile.profile_type_code, saveData.saving, direction, clearErrors, isInternalUser])

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
    }
  }, [currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, saveResultState.status])

  const keys = Object.keys(attributeData)

  const utilitiesHeadings = [
    <th title={t("titleHeat")} id='heat' key="inclusions-column-1-header" className='pier-datatable-centered-th'>{t("headerHeat")}</th>,
    <th title={t("titleHydro")} id='hydro' key="inclusions-column-2-header" className='pier-datatable-centered-th'>{t("headerHydro")}</th>,
    <th title={t("titleWater")} id='water' key="inclusions-column-3-header" className='pier-datatable-centered-th'>{t("headerWater")}</th>,
    <th title={t("titleCable")} id='cable' key="inclusions-column-4-header" className='pier-datatable-centered-th'>{t("headerCable")}</th>,
    <th title={t("titleInternet")} id='internet' key="inclusions-column-5-header" className='pier-datatable-centered-th'>{t("headerInternet")}</th>,
    <th title={t("titleAirConditioning")} id='airConditioning' key="inclusions-column-6-header" className='pier-datatable-centered-th'>{t("headerAirConditioning")}</th>
  ]
  const utilities: TableCellData[] = [
    { name: keys.filter((key) => key.includes("Heat"))[0], inputType: "checkbox", header: "heat" },
    { name: keys.filter((key) => key.includes("Hydro"))[0], inputType: "checkbox", header: "hydro" },
    { name: keys.filter((key) => key.includes("Water"))[0], inputType: "checkbox", header: "water" },
    { name: keys.filter((key) => key.includes("Cable"))[0], inputType: "checkbox", header: "cable" },
    { name: keys.filter((key) => key.includes("Internet"))[0], inputType: "checkbox", header: "internet" },
    { name: keys.filter((key) => key.includes("AirConditioning"))[0], inputType: "checkbox", header: "airConditioning" }
  ]

  const amenitiesThead = <thead>
    <tr>
      <th rowSpan={2}
        title={t("titlePoolSaunaFitness")}
        id='poolSaunaFitness'
        key="inclusions-column-7-header"
        className='pier-datatable-centered-th pier-datatable-centered-heading'>
        {t("headerPoolSaunaFitness")}
      </th>
      <th rowSpan={2}
        title={t("titleElevator")}
        id='elevator'
        key="inclusions-column-8-header"
        className='pier-datatable-centered-th '>
        {t("headerElevator")}
      </th>
      <th title={t("")}
        id='laundry'
        key="inclusions-column-9-header"
        className='pier-datatable-centered-th'
        colSpan={2}>
        {t("laundry")}
      </th>
      <th title={t("")}
        id='storage'
        key="inclusions-column-10-header"
        className='pier-datatable-centered-th pier-datatable-centered-heading'
        colSpan={2}>
        {t("storage")}
      </th>
      <th title={t("")}
        id='access'
        key="inclusions-column-11-header"
        className='pier-datatable-centered-th pier-datatable-centered-heading'
        colSpan={2}>
        {t("access")}
      </th>
      <th title={t("")}
        id='onsite'
        key="inclusions-column-12-header"
        className='pier-datatable-centered-th pier-datatable-centered-heading'
        colSpan={2}>
        {t("onsite")}
      </th>
    </tr>
    <tr>
      <th title={t("titleLaundryEnsuite")}
        id='laundryEnsuite'
        key="inclusions-column-1-header"
        className='pier-datatable-centered-subheading laundry-subheading'>
        {isInternalUser ? t("headerLaundryEnsuiteIntUser") : t("headerLaundryEnsuite")}
      </th>
      <th title={t("titleLaundryCommon")}
        id='laundryCommon'
        key="inclusions-column-2-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerLaundryCommonIntUser") : t("headerLaundryCommon")}
      </th>

      <th title={t("titleStorageInternal")}
        id='storageInternal'
        key="inclusions-column-3-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerStorageInternalIntUser") : t("headerStorageInternal")}
      </th>
      <th title={t("titleStorageExternal")}
        id='storageExternal'
        key="inclusions-column-4-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerStorageExternalIntUser") : t("headerStorageExternal")}
      </th>

      <th title={t("titleAccessDoormanConcierge")}
        id='accessDoormanConcierge'
        key="inclusions-column-5-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerAccessDoormanConciergeIntUser") : t("headerAccessDoormanConcierge")}
      </th>
      <th title={t("titleAccessControlled")}
        id='accessControlled'
        key="inclusions-column-6-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerAccessControlledIntUser") : t("headerAccessControlled")}
      </th>

      <th title={t("titleOnsiteSecurity")}
        id='onsiteSecurity'
        key="inclusions-column-7-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerOnsiteSecurityIntUser") : t("headerOnsiteSecurity")}
      </th>
      <th title={t("titleOnsiteStaff")}
        id='onsiteStaff'
        key="inclusions-column-8-header"
        className='pier-datatable-centered-subheading'>
        {isInternalUser ? t("headerOnsiteStaffIntUser") : t("headerOnsiteStaff")}
      </th>
    </tr>
  </thead>

const amenitiesInclusions: TableCellData[] = [
    { name: keys.filter((key) => key.includes("PoolSaunaFitness"))[0], inputType: "checkbox", header: "poolSaunaFitness" },
    { name: keys.filter((key) => key.includes("Elevator"))[0], inputType: "checkbox", header: "elevator" },
    { name: keys.filter((key) => key.includes("LaundryEnsuite"))[0], inputType: "checkbox", header: "LaundryEnsuite" },
    { name: keys.filter((key) => key.includes("LaundryCommon"))[0], inputType: "checkbox", header: "LaundryCommon" },
    { name: keys.filter((key) => key.includes("StorageInternal"))[0], inputType: "checkbox", header: "StorageInternal" },
    { name: keys.filter((key) => key.includes("StorageExternal"))[0], inputType: "checkbox", header: "StorageExternal" },
    { name: keys.filter((key) => key.includes("AccessDoormanConcierge"))[0], inputType: "checkbox", header: "AccessDoormanConcierge" },
    { name: keys.filter((key) => key.includes("AccessControlled"))[0], inputType: "checkbox", header: "AccessControlled" },
    { name: keys.filter((key) => key.includes("OnsiteSecurity"))[0], inputType: "checkbox", header: "OnsiteSecurity" },
    { name: keys.filter((key) => key.includes("OnsiteStaff"))[0], inputType: "checkbox", header: "OnsiteStaff" },
  ]

  return (
    <>
      <div className='partial-width'>
        <ErrorMessages errors={errors} />

        <p className='strong'>{t("headerAmenities")}</p>
        <DataTable headings={utilitiesHeadings} className='utilities-table'>
          <TableRow
            paddedRow
            cellData={utilities}
            dataType={{ ...attributeData } as Record<string, boolean | null>}
            onChange={handleAttributeChange} />
        </DataTable>
        <p></p>
        <DataTable thead={amenitiesThead} className='amenities-table'>
          <TableRow
            paddedRow
            cellData={amenitiesInclusions}
            dataType={{ ...attributeData } as Record<string, boolean | null>}
            onChange={handleAttributeChange} />
        </DataTable>
      </div>
    </>
  )
}

export default Utilities
